import { useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, errMsg, type Realm } from "../api";
import { Brand } from "../components/Brand";
import { TwoFactorEnroll } from "../components/TwoFactorEnroll";
import { Btn, Field, Input, Notice } from "../components/ui";
import { MoonIcon, SunIcon } from "../components/Icons";
import { THEME_LABEL, useTheme, type ThemePref } from "../theme";

const TITLES: Record<Realm, string> = { pf: "Entrar — Pessoa Física", pj: "Entrar — Pessoa Jurídica", admin: "Acesso administrativo" };
const ERRORS: Record<string, string> = {
  email_ja_cadastrado_com_senha: "Este e-mail já tem cadastro com senha neste ambiente. Entre com e-mail e senha.",
  google_estado_invalido: "A verificação do login com Google expirou. Tente novamente.",
  google_falha: "Não foi possível entrar com o Google. Tente novamente.",
  google_email_nao_verificado: "O e-mail da conta Google não está verificado.",
  cadastro_indisponivel: "O cadastro de novos clientes não está disponível neste ambiente.",
};

export function Login({ realm }: { realm: Realm }) {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const { pref, resolved, setPref } = useTheme();
  const nextPref: ThemePref = pref === "claro" ? "escuro" : pref === "escuro" ? "automatico" : "claro";
  const [step, setStep] = useState<"cred" | "mfa" | "enroll">(params.get("mfa") === "1" ? "mfa" : params.get("mfa") === "enroll" ? "enroll" : "cred");
  const [challenge, setChallenge] = useState<string | undefined>();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [useRecovery, setUseRecovery] = useState(false);
  const [err, setErr] = useState(ERRORS[params.get("erro") ?? ""] ?? "");
  const [busy, setBusy] = useState(false);
  const [google, setGoogle] = useState(false);
  const home = realm === "admin" ? "/admin" : `/${realm}`;

  useEffect(() => {
    if (realm === "admin") return;
    api<{ enabled: boolean }>("GET", `/api/${realm}/auth/google/status`).then((r) => setGoogle(r.enabled)).catch(() => setGoogle(false));
  }, [realm]);

  async function submitCred(e: FormEvent) {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const r = await api<{ status: string; challenge?: string }>("POST", `/api/${realm}/auth/login`, { email, password });
      if (r.status === "ok") return nav(home, { replace: true });
      setChallenge(r.challenge);
      setStep(r.status === "mfa_required" ? "mfa" : "enroll");
    } catch (e2) { setErr(errMsg(e2)); } finally { setBusy(false); }
  }

  async function submitMfa(e: FormEvent) {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const body = useRecovery ? { challenge, recoveryCode: code } : { challenge, code: code.replace(/\s/g, "") };
      await api("POST", `/api/${realm}/auth/mfa/verify`, body);
      nav(home, { replace: true });
    } catch (e2) { setErr(errMsg(e2)); } finally { setBusy(false); }
  }

  return (
    <div className="auth-wrap">
      <div className="auth-card card">
        <Brand />
        <div className="auth-top">
          <button className="icon-btn" onClick={() => setPref(nextPref)} aria-label={`Tema: ${THEME_LABEL[pref]} (alternar para ${THEME_LABEL[nextPref]})`} title={`Tema: ${THEME_LABEL[pref]}`} style={{ color: "var(--text-muted)" }}>
            {resolved === "dark" ? <SunIcon /> : <MoonIcon />}
          </button>
        </div>
        {realm !== "admin" && step === "cred" && (
          <div className="realm-tabs">
            <Link to="/pf/entrar" className={realm === "pf" ? "on" : ""}>Pessoa Física</Link>
            <Link to="/pj/entrar" className={realm === "pj" ? "on" : ""}>Pessoa Jurídica</Link>
          </div>
        )}
        <h1>{step === "enroll" ? "Configure o 2FA" : step === "mfa" ? "Verificação em duas etapas" : TITLES[realm]}</h1>
        {realm !== "admin" && step === "cred" && <p className="muted" style={{ marginTop: 0 }}>Cada ambiente tem credenciais e sessão próprias.</p>}
        {step === "enroll" && <p className="muted" style={{ marginTop: 0 }}>{realm === "admin" ? "O 2FA é obrigatório para perfis administrativos." : "Por segurança, a verificação em dois passos é obrigatória neste ambiente."} Configure agora para concluir o acesso.</p>}
        {params.get("senha") === "redefinida" && !err && <Notice kind="ok">Senha redefinida. Entre com a nova senha.</Notice>}
        {err && <Notice kind="error">{err}</Notice>}

        {step === "cred" && (
          <form onSubmit={submitCred}>
            <Field label="E-mail"><Input type="email" autoComplete="username" required value={email} onChange={(e) => setEmail(e.target.value)} /></Field>
            <Field label="Senha"><Input type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} /></Field>
            {realm !== "admin" && <p style={{ textAlign: "right", margin: "-4px 0 12px" }}><Link to={`/${realm}/esqueci-senha`}>Esqueci minha senha</Link></p>}
            <Btn type="submit" block disabled={busy}>Entrar</Btn>
            {google && (
              <>
                <div className="divider">ou</div>
                <a className="btn ghost block" style={{ display: "block", textAlign: "center", textDecoration: "none" }} href={`/api/${realm}/auth/google/start`}>Entrar com Google</a>
              </>
            )}
            {realm !== "admin" && <p className="muted" style={{ textAlign: "center", marginBottom: 0 }}>Novo por aqui? <Link to={`/${realm}/cadastro`}>Criar usuário</Link></p>}
          </form>
        )}

        {step === "mfa" && (
          <form onSubmit={submitMfa}>
            <Field label={useRecovery ? "Código de recuperação" : "Código do aplicativo autenticador"}>
              <Input autoFocus inputMode={useRecovery ? "text" : "numeric"} autoComplete="one-time-code" required value={code} onChange={(e) => setCode(e.target.value)} />
            </Field>
            <Btn type="submit" block disabled={busy}>Verificar</Btn>
            <p style={{ textAlign: "center", marginBottom: 0 }}>
              <a href="#" onClick={(e) => { e.preventDefault(); setUseRecovery(!useRecovery); setCode(""); }}>
                {useRecovery ? "Usar o aplicativo autenticador" : "Usar um código de recuperação"}
              </a>
            </p>
          </form>
        )}

        {step === "enroll" && <TwoFactorEnroll realm={realm} challenge={challenge} onFinished={() => nav(home, { replace: true })} />}
      </div>
    </div>
  );
}
